import React, { useState, useEffect } from 'react';

const BrowserCompatibilityTester = () => {
  const [testResults, setTestResults] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [currentTest, setCurrentTest] = useState('');
  const [browserInfo, setBrowserInfo] = useState({});

  useEffect(() => {
    detectBrowserInfo();
  }, []);

  const detectBrowserInfo = () => {
    const userAgent = navigator.userAgent;
    const browserInfo = {
      userAgent,
      platform: navigator.platform,
      language: navigator.language,
      cookieEnabled: navigator.cookieEnabled,
      onLine: navigator.onLine,
      screenWidth: screen.width,
      screenHeight: screen.height,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      devicePixelRatio: window.devicePixelRatio,
      touchSupport: 'ontouchstart' in window,
      webGLSupport: checkWebGLSupport(),
      localStorageSupport: checkLocalStorageSupport(),
      sessionStorageSupport: checkSessionStorageSupport(),
      indexedDBSupport: checkIndexedDBSupport(),
      webWorkersSupport: checkWebWorkersSupport(),
      serviceWorkerSupport: checkServiceWorkerSupport(),
      es6Support: checkES6Support(),
      cssGridSupport: checkCSSGridSupport(),
      flexboxSupport: checkFlexboxSupport(),
      cssVariablesSupport: checkCSSVariablesSupport(),
      webpSupport: checkWebPSupport(),
      avifSupport: checkAVIFSupport()
    };
    setBrowserInfo(browserInfo);
  };

  const checkWebGLSupport = () => {
    try {
      const canvas = document.createElement('canvas');
      return !!(window.WebGLRenderingContext && canvas.getContext('webgl'));
    } catch (e) {
      return false;
    }
  };

  const checkLocalStorageSupport = () => {
    try {
      const test = 'test';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  };

  const checkSessionStorageSupport = () => {
    try {
      const test = 'test';
      sessionStorage.setItem(test, test);
      sessionStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  };

  const checkIndexedDBSupport = () => {
    return 'indexedDB' in window;
  };

  const checkWebWorkersSupport = () => {
    return typeof Worker !== 'undefined';
  };

  const checkServiceWorkerSupport = () => {
    return 'serviceWorker' in navigator;
  };

  const checkES6Support = () => {
    try {
      eval('class Test {}');
      eval('const test = () => {}');
      eval('let test = 1');
      return true;
    } catch (e) {
      return false;
    }
  };

  const checkCSSGridSupport = () => {
    return CSS.supports('display', 'grid');
  };

  const checkFlexboxSupport = () => {
    return CSS.supports('display', 'flex');
  };

  const checkCSSVariablesSupport = () => {
    return CSS.supports('--custom-property', 'value');
  };

  const checkWebPSupport = () => {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
  };

  const checkAVIFSupport = () => {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    return canvas.toDataURL('image/avif').indexOf('data:image/avif') === 0;
  };

  const addTestResult = (testName, status, details = '', browser = '') => {
    const result = {
      id: Date.now() + Math.random(),
      testName,
      status, // 'pass', 'fail', 'warning', 'info'
      details,
      browser: browser || getBrowserName(),
      timestamp: new Date().toISOString()
    };
    setTestResults(prev => [...prev, result]);
  };

  const getBrowserName = () => {
    const userAgent = navigator.userAgent;
    if (userAgent.includes('Chrome')) return 'Chrome';
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Safari')) return 'Safari';
    if (userAgent.includes('Edge')) return 'Edge';
    if (userAgent.includes('Opera')) return 'Opera';
    return 'Unknown';
  };

  const clearResults = () => {
    setTestResults([]);
  };

  // Test 1: Basic JavaScript Features
  const testJavaScriptFeatures = async () => {
    setCurrentTest('JavaScript Features');
    
    // Test ES6 features
    try {
      // Arrow functions
      const arrowFunc = () => 'test';
      if (typeof arrowFunc === 'function') {
        addTestResult('Arrow Functions', 'pass', 'ES6 arrow functions supported');
      } else {
        addTestResult('Arrow Functions', 'fail', 'ES6 arrow functions not supported');
      }

      // Template literals
      const template = `test ${1 + 1}`;
      if (template === 'test 2') {
        addTestResult('Template Literals', 'pass', 'ES6 template literals supported');
      } else {
        addTestResult('Template Literals', 'fail', 'ES6 template literals not supported');
      }

      // Destructuring
      const { test } = { test: 'value' };
      if (test === 'value') {
        addTestResult('Destructuring', 'pass', 'ES6 destructuring supported');
      } else {
        addTestResult('Destructuring', 'fail', 'ES6 destructuring not supported');
      }

      // Async/await
      const asyncTest = async () => 'test';
      if (typeof asyncTest === 'function') {
        addTestResult('Async/Await', 'pass', 'ES6 async/await supported');
      } else {
        addTestResult('Async/Await', 'fail', 'ES6 async/await not supported');
      }

    } catch (error) {
      addTestResult('JavaScript Features', 'fail', `Error: ${error.message}`);
    }
  };

  // Test 2: DOM APIs
  const testDOMAPIs = async () => {
    setCurrentTest('DOM APIs');
    
    try {
      // QuerySelector
      if (document.querySelector) {
        addTestResult('QuerySelector', 'pass', 'querySelector API supported');
      } else {
        addTestResult('QuerySelector', 'fail', 'querySelector API not supported');
      }

      // AddEventListener
      if (document.addEventListener) {
        addTestResult('AddEventListener', 'pass', 'addEventListener API supported');
      } else {
        addTestResult('AddEventListener', 'fail', 'addEventListener API not supported');
      }

      // Fetch API
      if (window.fetch) {
        addTestResult('Fetch API', 'pass', 'Fetch API supported');
      } else {
        addTestResult('Fetch API', 'fail', 'Fetch API not supported');
      }

      // Promise
      if (window.Promise) {
        addTestResult('Promise', 'pass', 'Promise API supported');
      } else {
        addTestResult('Promise', 'fail', 'Promise API not supported');
      }

    } catch (error) {
      addTestResult('DOM APIs', 'fail', `Error: ${error.message}`);
    }
  };

  // Test 3: CSS Features
  const testCSSFeatures = async () => {
    setCurrentTest('CSS Features');
    
    try {
      // CSS Grid
      if (CSS.supports('display', 'grid')) {
        addTestResult('CSS Grid', 'pass', 'CSS Grid supported');
      } else {
        addTestResult('CSS Grid', 'warning', 'CSS Grid not supported - fallback to flexbox');
      }

      // Flexbox
      if (CSS.supports('display', 'flex')) {
        addTestResult('Flexbox', 'pass', 'Flexbox supported');
      } else {
        addTestResult('Flexbox', 'fail', 'Flexbox not supported - layout issues expected');
      }

      // CSS Variables
      if (CSS.supports('--custom-property', 'value')) {
        addTestResult('CSS Variables', 'pass', 'CSS Variables supported');
      } else {
        addTestResult('CSS Variables', 'warning', 'CSS Variables not supported - theme switching may not work');
      }

      // CSS Transforms
      if (CSS.supports('transform', 'translateX(10px)')) {
        addTestResult('CSS Transforms', 'pass', 'CSS Transforms supported');
      } else {
        addTestResult('CSS Transforms', 'warning', 'CSS Transforms not supported - animations may not work');
      }

    } catch (error) {
      addTestResult('CSS Features', 'fail', `Error: ${error.message}`);
    }
  };

  // Test 4: Storage APIs
  const testStorageAPIs = async () => {
    setCurrentTest('Storage APIs');
    
    try {
      // LocalStorage
      if (browserInfo.localStorageSupport) {
        addTestResult('LocalStorage', 'pass', 'LocalStorage supported');
      } else {
        addTestResult('LocalStorage', 'fail', 'LocalStorage not supported - data persistence will not work');
      }

      // SessionStorage
      if (browserInfo.sessionStorageSupport) {
        addTestResult('SessionStorage', 'pass', 'SessionStorage supported');
      } else {
        addTestResult('SessionStorage', 'warning', 'SessionStorage not supported - session data will not persist');
      }

      // IndexedDB
      if (browserInfo.indexedDBSupport) {
        addTestResult('IndexedDB', 'pass', 'IndexedDB supported');
      } else {
        addTestResult('IndexedDB', 'warning', 'IndexedDB not supported - advanced caching not available');
      }

    } catch (error) {
      addTestResult('Storage APIs', 'fail', `Error: ${error.message}`);
    }
  };

  // Test 5: Modern Web APIs
  const testModernWebAPIs = async () => {
    setCurrentTest('Modern Web APIs');
    
    try {
      // Web Workers
      if (browserInfo.webWorkersSupport) {
        addTestResult('Web Workers', 'pass', 'Web Workers supported');
      } else {
        addTestResult('Web Workers', 'warning', 'Web Workers not supported - background processing limited');
      }

      // Service Workers
      if (browserInfo.serviceWorkerSupport) {
        addTestResult('Service Workers', 'pass', 'Service Workers supported - PWA features available');
      } else {
        addTestResult('Service Workers', 'warning', 'Service Workers not supported - PWA features not available');
      }

      // WebGL
      if (browserInfo.webGLSupport) {
        addTestResult('WebGL', 'pass', 'WebGL supported - 3D graphics available');
      } else {
        addTestResult('WebGL', 'warning', 'WebGL not supported - 3D graphics not available');
      }

      // Touch Events
      if (browserInfo.touchSupport) {
        addTestResult('Touch Events', 'pass', 'Touch events supported - mobile optimized');
      } else {
        addTestResult('Touch Events', 'info', 'Touch events not supported - desktop only');
      }

    } catch (error) {
      addTestResult('Modern Web APIs', 'fail', `Error: ${error.message}`);
    }
  };

  // Test 6: Calculator Functionality
  const testCalculatorFunctionality = async () => {
    setCurrentTest('Calculator Functionality');
    
    try {
      // Test basic math operations
      const mathTest = 2 + 2;
      if (mathTest === 4) {
        addTestResult('Basic Math', 'pass', 'Basic math operations working');
      } else {
        addTestResult('Basic Math', 'fail', 'Basic math operations not working');
      }

      // Test decimal operations
      const decimalTest = 0.1 + 0.2;
      if (Math.abs(decimalTest - 0.3) < 0.0001) {
        addTestResult('Decimal Math', 'pass', 'Decimal math operations working');
      } else {
        addTestResult('Decimal Math', 'warning', 'Decimal math precision issues detected');
      }

      // Test large numbers
      const largeNumberTest = 999999999 + 1;
      if (largeNumberTest === 1000000000) {
        addTestResult('Large Numbers', 'pass', 'Large number operations working');
      } else {
        addTestResult('Large Numbers', 'warning', 'Large number precision issues detected');
      }

      // Test date operations
      const dateTest = new Date('2025-01-15');
      if (dateTest instanceof Date && !isNaN(dateTest.getTime())) {
        addTestResult('Date Operations', 'pass', 'Date operations working');
      } else {
        addTestResult('Date Operations', 'fail', 'Date operations not working');
      }

    } catch (error) {
      addTestResult('Calculator Functionality', 'fail', `Error: ${error.message}`);
    }
  };

  // Test 7: Performance
  const testPerformance = async () => {
    setCurrentTest('Performance');
    
    try {
      // Test memory usage
      if (performance.memory) {
        const memory = performance.memory;
        const usedMB = Math.round(memory.usedJSHeapSize / 1024 / 1024);
        const totalMB = Math.round(memory.totalJSHeapSize / 1024 / 1024);
        
        if (usedMB < 100) {
          addTestResult('Memory Usage', 'pass', `Memory usage: ${usedMB}MB / ${totalMB}MB`);
        } else if (usedMB < 200) {
          addTestResult('Memory Usage', 'warning', `Memory usage: ${usedMB}MB / ${totalMB}MB - high usage`);
        } else {
          addTestResult('Memory Usage', 'fail', `Memory usage: ${usedMB}MB / ${totalMB}MB - very high usage`);
        }
      } else {
        addTestResult('Memory Usage', 'info', 'Memory API not available');
      }

      // Test timing
      const startTime = performance.now();
      await new Promise(resolve => setTimeout(resolve, 10));
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      if (duration < 20) {
        addTestResult('Timing Performance', 'pass', `Timing accuracy: ${duration.toFixed(2)}ms`);
      } else {
        addTestResult('Timing Performance', 'warning', `Timing accuracy: ${duration.toFixed(2)}ms - may be slow`);
      }

    } catch (error) {
      addTestResult('Performance', 'fail', `Error: ${error.message}`);
    }
  };

  // Run all tests
  const runAllTests = async () => {
    setIsRunning(true);
    clearResults();
    
    const tests = [
      testJavaScriptFeatures,
      testDOMAPIs,
      testCSSFeatures,
      testStorageAPIs,
      testModernWebAPIs,
      testCalculatorFunctionality,
      testPerformance
    ];

    for (const test of tests) {
      try {
        await test();
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (error) {
        addTestResult('Test Execution', 'fail', `Test failed: ${error.message}`);
      }
    }

    setIsRunning(false);
    setCurrentTest('');
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'pass': return 'text-green-600 bg-green-100';
      case 'fail': return 'text-red-600 bg-red-100';
      case 'warning': return 'text-yellow-600 bg-yellow-100';
      case 'info': return 'text-blue-600 bg-blue-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'pass': return '✓';
      case 'fail': return '✗';
      case 'warning': return '⚠';
      case 'info': return 'ℹ';
      default: return '?';
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Browser Compatibility Testing Suite</h1>
        
        {/* Browser Info */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Browser Information</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            <div><strong>Browser:</strong> {getBrowserName()}</div>
            <div><strong>Platform:</strong> {browserInfo.platform}</div>
            <div><strong>Language:</strong> {browserInfo.language}</div>
            <div><strong>Screen:</strong> {browserInfo.screenWidth}x{browserInfo.screenHeight}</div>
            <div><strong>Window:</strong> {browserInfo.windowWidth}x{browserInfo.windowHeight}</div>
            <div><strong>Pixel Ratio:</strong> {browserInfo.devicePixelRatio}</div>
            <div><strong>Touch:</strong> {browserInfo.touchSupport ? 'Yes' : 'No'}</div>
            <div><strong>Online:</strong> {browserInfo.onLine ? 'Yes' : 'No'}</div>
            <div><strong>Cookies:</strong> {browserInfo.cookieEnabled ? 'Enabled' : 'Disabled'}</div>
          </div>
        </div>
        
        {/* Test Controls */}
        <div className="mb-6">
          <div className="flex gap-4 mb-4">
            <button
              onClick={runAllTests}
              disabled={isRunning}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {isRunning ? 'Running Tests...' : 'Run All Tests'}
            </button>
            
            <button
              onClick={clearResults}
              disabled={isRunning}
              className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              Clear Results
            </button>
          </div>
          
          {isRunning && (
            <div className="text-blue-600 font-medium">
              Currently running: {currentTest}
            </div>
          )}
        </div>

        {/* Test Results */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Test Results</h2>
          
          {testResults.length === 0 ? (
            <div className="text-gray-500 text-center py-8">
              No tests run yet. Click "Run All Tests" to start testing browser compatibility.
            </div>
          ) : (
            <div className="space-y-3">
              {testResults.map((result) => (
                <div key={result.id} className="flex items-start p-4 border rounded-lg">
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-4 ${getStatusColor(result.status)}`}>
                    {getStatusIcon(result.status)}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{result.testName}</div>
                    <div className="text-sm text-gray-600 mt-1">{result.details}</div>
                    <div className="text-xs text-gray-400 mt-1">
                      {result.browser} • {new Date(result.timestamp).toLocaleString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Summary */}
        {testResults.length > 0 && (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-2">Test Summary</h3>
            <div className="grid grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-green-600 font-medium">
                  Passed: {testResults.filter(r => r.status === 'pass').length}
                </span>
              </div>
              <div>
                <span className="text-red-600 font-medium">
                  Failed: {testResults.filter(r => r.status === 'fail').length}
                </span>
              </div>
              <div>
                <span className="text-yellow-600 font-medium">
                  Warnings: {testResults.filter(r => r.status === 'warning').length}
                </span>
              </div>
              <div>
                <span className="text-blue-600 font-medium">
                  Info: {testResults.filter(r => r.status === 'info').length}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Browser Support Matrix */}
        <div className="mt-8 p-4 bg-blue-50 rounded-lg">
          <h3 className="font-semibold text-blue-900 mb-2">Supported Browsers</h3>
          <div className="text-sm text-blue-800 space-y-1">
            <p>• <strong>Chrome:</strong> 80+ (Full support)</p>
            <p>• <strong>Firefox:</strong> 75+ (Full support)</p>
            <p>• <strong>Safari:</strong> 13+ (Full support)</p>
            <p>• <strong>Edge:</strong> 80+ (Full support)</p>
            <p>• <strong>Mobile Safari:</strong> 13+ (Full support)</p>
            <p>• <strong>Chrome Mobile:</strong> 80+ (Full support)</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrowserCompatibilityTester;
